import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import { notify } from '@/lib/notify.js';

import { useCredentialAction } from '../../hooks/useCredentialAction.js';
import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

/**
 * GitLab is token-only (a personal access token created under User Settings →
 * Access Tokens), plus a REQUIRED instance URL so self-hosted GitLab works —
 * the field is pre-filled with the SaaS default `https://gitlab.com`. Identity
 * stores the URL on the connection; there is no deployment default, so a
 * connection without it is unusable.
 */

interface FormErrors {
  token?: string;
  instanceUrl?: string;
  submit?: string;
}

/**
 * Client-side mirror of identity's `instance_url` rule: the value must parse
 * as an absolute URL with an http(s) scheme. The server re-validates and its
 * 400 message is surfaced verbatim on submit.
 */
function isAbsoluteHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function validate(token: string, instanceUrl: string): FormErrors {
  const errors: FormErrors = {};
  if (token.length < 8) {
    errors.token = 'Token looks too short';
  }
  if (instanceUrl.length === 0) {
    errors.instanceUrl = 'Instance URL is required';
  } else if (!isAbsoluteHttpUrl(instanceUrl)) {
    errors.instanceUrl =
      'Enter an absolute http(s) URL (e.g. https://gitlab.com)';
  }
  return errors;
}

export function GitlabCredentialsForm({
  onSaved,
  onCancel,
  details,
  explainUnavailableActions = false,
  onBusyChange,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it, plus which lane it
   * lands in. Absent on a first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
  explainUnavailableActions?: boolean;
  onBusyChange?: (busy: boolean) => void;
}): JSX.Element {
  const [token, setToken] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('https://gitlab.com');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();
  const action = useCredentialAction(
    explainUnavailableActions,
    `${workspaceId ?? ''}:${details?.connectionId ?? 'new'}`,
    onBusyChange,
  );

  const handleTokenChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setToken(e.target.value);
    if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
  };

  const handleInstanceUrlChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setInstanceUrl(e.target.value);
    if (errors.instanceUrl)
      setErrors((es) => ({ ...es, instanceUrl: undefined }));
  };

  /** Reads the clipboard into the token field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    if (action.blocked()) return;
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setToken(text.trim());
      if (errors.token) setErrors((es) => ({ ...es, token: undefined }));
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (action.blocked()) return;
    const trimmedToken = token.trim();
    const trimmedUrl = instanceUrl.trim();
    const found = validate(trimmedToken, trimmedUrl);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      if (explainUnavailableActions)
        notify.error(
          Object.values(found)[0] ?? 'Complete the required connection fields.',
        );
      return;
    }
    const ticket = action.begin();
    if (!ticket) return;
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveGitlabCredentials(
        { token: trimmedToken, instance_url: trimmedUrl },
        workspaceId,
        details,
      );
      action.complete(ticket, onSaved);
    } catch (err) {
      if (!action.isCurrent(ticket)) return;
      const message =
        err instanceof Error ? err.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      if (action.isCurrent(ticket)) setSubmitting(false);
      action.end(ticket);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="gitlab-instance-url" className="text-xs">
          Instance URL
        </Label>
        <Input
          id="gitlab-instance-url"
          type="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="https://gitlab.com"
          value={instanceUrl}
          onChange={handleInstanceUrlChange}
          disabled={submitting}
          aria-invalid={Boolean(errors.instanceUrl)}
          aria-describedby={
            errors.instanceUrl
              ? 'gitlab-instance-url-err'
              : 'gitlab-instance-url-help'
          }
        />
        {errors.instanceUrl ? (
          <p id="gitlab-instance-url-err" className="text-xs text-destructive">
            {errors.instanceUrl}
          </p>
        ) : (
          <p
            id="gitlab-instance-url-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            Keep <span className="font-medium">https://gitlab.com</span> for
            GitLab SaaS, or enter your self-hosted GitLab URL.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="gitlab-token" className="text-xs">
          Access token
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="gitlab-token"
              type={showToken ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="GitLab personal access token"
              value={token}
              onChange={handleTokenChange}
              disabled={submitting}
              aria-invalid={Boolean(errors.token)}
              aria-describedby={
                errors.token ? 'gitlab-token-err' : 'gitlab-token-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              aria-label={showToken ? 'Hide token' : 'Show token'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showToken ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={!explainUnavailableActions && submitting}
            aria-label="Paste from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.token ? (
          <p id="gitlab-token-err" className="text-xs text-destructive">
            {errors.token}
          </p>
        ) : (
          <p
            id="gitlab-token-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            In GitLab, open{' '}
            <span className="font-medium">User Settings → Access Tokens</span>{' '}
            and create a token with the <span className="font-medium">api</span>{' '}
            or <span className="font-medium">read_api</span> scope. Paste it
            here.
          </p>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => action.cancel(onCancel)}
          disabled={!explainUnavailableActions && submitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!explainUnavailableActions && submitting}
        >
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
